/**
 * WebGL-based mask renderer for efficiently displaying and updating segmentation masks.
 * This renderer uses WebGL2 to render mask data with customizable colors for different labels.
 * It supports efficient partial updates and handles camera transformations.
 */

import type { ImageRaster } from '@/modules/Editor/models/raster/ImageRaster'
import type { IPoint } from '@/core/annotations'
import { RasterTooLargeError } from '@/modules/Editor/models/raster/deserializeImageRaster'

/**
 * Vertex shader that positions vertices and passes texture coordinates to the fragment shader.
 * Takes vertex positions and texture coordinates as inputs and outputs texture coordinates.
 *
 * WebGL Concepts:
 * - in: Input attribute from JavaScript
 * - out: Output varying that will be interpolated and passed to fragment shader
 * - gl_Position: Special output variable that determines vertex position in clip space
 */
const vsSource = `#version 300 es
  in vec4 aVertexPosition;  // Vertex position in clip space (-1 to 1)
  in vec2 aTextureCoord;    // Texture coordinates (0 to 1)

  out highp vec2 vTextureCoord;  // Interpolated texture coordinates for fragment shader

  void main(void) {
    gl_Position = aVertexPosition;  // Set vertex position in clip space
    vTextureCoord = aTextureCoord;  // Pass texture coordinates to fragment shader
  }
`

/**
 * Fragment shader that handles the actual mask rendering.
 * Takes a mask texture (containing label indices) and a color map texture (containing colors for each label).
 * Outputs the final color based on the label at each pixel.
 *
 * WebGL Concepts:
 * - precision: Sets floating-point precision for fragment shader
 * - uniform: Read-only values passed from JavaScript
 * - sampler2D: Texture sampler for 2D textures
 * - discard: Discards the current fragment (no output)
 * - out: Output color for the fragment
 */
const fsSource = `#version 300 es
  precision mediump float;  // Set medium precision for floating-point calculations
  in highp vec2 vTextureCoord;  // Interpolated texture coordinates from vertex shader
  uniform sampler2D uMaskTexture;     // Texture with label indices (0-255 normalized)
  uniform sampler2D uColorMapTexture; // 1D Texture with RGBA colors for each label
  uniform float uMaxLabels;         // Maximum number of labels (e.g., 256)

  uniform vec2 uTextureOrigin;      // Top-left of visible source region in normalized texture coords
  uniform vec2 uTextureViewSize;    // Size of visible source region in normalized texture coords

  out vec4 fragColor;  // Output color for the fragment

  void main(void) {
    // Calculate actual texture coordinates based on camera transform
    vec2 actualTexCoord = uTextureOrigin + vTextureCoord * uTextureViewSize;

    // Discard fragments outside the [0, 1] range (outside the image)
    if (actualTexCoord.x < 0.0 || actualTexCoord.x > 1.0 ||
        actualTexCoord.y < 0.0 || actualTexCoord.y > 1.0) {
      discard;  // Don't render this fragment
      return;
    }

    // Sample the mask texture to get the label index
    float maskValue = texture(uMaskTexture, actualTexCoord).r;
    if (maskValue == 0.0) {
      discard;  // Don't render transparent areas
      return;
    }

    // Convert normalized mask value to label index
    float labelIndex = maskValue * (uMaxLabels - 1.0);
    // Calculate texture coordinate for color lookup (add 0.5 for better sampling)
    float colorMapTexCoordS = (labelIndex + 0.5) / uMaxLabels;
    // Sample the color map texture to get the final color
    fragColor = texture(uColorMapTexture, vec2(colorMapTexCoordS, 0.5));
  }
`

/**
 * Loads and compiles a WebGL shader.
 * @param gl - WebGL2 rendering context
 * @param type - Type of shader (VERTEX_SHADER or FRAGMENT_SHADER)
 * @param source - GLSL source code for the shader
 * @returns Compiled shader or null if compilation fails
 */
function loadShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  // Create a new shader object
  const shader = gl.createShader(type)
  if (!shader) {
    return null
  }

  // Set the shader source code
  gl.shaderSource(shader, source)
  // Compile the shader
  gl.compileShader(shader)

  // Check if compilation was successful
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn('Shader compilation error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }

  return shader
}

/**
 * Creates and links a WebGL shader program from vertex and fragment shaders.
 * @param gl - WebGL2 rendering context
 * @param vsSrc - Vertex shader source code
 * @param fsSrc - Fragment shader source code
 * @returns Linked shader program or null if linking fails
 */
function initShaderProgram(
  gl: WebGL2RenderingContext,
  vsSrc: string,
  fsSrc: string,
): WebGLProgram | null {
  // Create and compile both shaders
  const vertexShader = loadShader(gl, gl.VERTEX_SHADER, vsSrc)
  const fragmentShader = loadShader(gl, gl.FRAGMENT_SHADER, fsSrc)

  if (!vertexShader || !fragmentShader) {
    if (vertexShader) gl.deleteShader(vertexShader)
    if (fragmentShader) gl.deleteShader(fragmentShader)
    return null
  }

  // Create a shader program
  const shaderProgram = gl.createProgram()
  if (!shaderProgram) {
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
    return null
  }

  // Attach both shaders to the program
  gl.attachShader(shaderProgram, vertexShader)
  gl.attachShader(shaderProgram, fragmentShader)
  // Link the program
  gl.linkProgram(shaderProgram)

  // Check if linking was successful
  if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
    console.warn('Program linking error:', gl.getProgramInfoLog(shaderProgram))
    gl.deleteProgram(shaderProgram)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
    return null
  }

  // Clean up shaders as they're no longer needed
  gl.deleteShader(vertexShader)
  gl.deleteShader(fragmentShader)
  return shaderProgram
}

function unpackSegmentColorToRGBA(value: number): Uint8Array {
  const uint32 = new Uint32Array(1)
  uint32[0] = value

  const rgba = new Uint8Array(uint32.buffer)

  // Return a copy to avoid mutation issues
  return new Uint8Array([rgba[0], rgba[1], rgba[2], rgba[3]])
}

/**
 * Creates a 1D texture containing the color mapping for each label.
 * The texture is used as a lookup table to map label indices to colors.
 * @param gl - WebGL2 rendering context
 * @param labelColorMap - Object mapping label indices to packed RGBA colors
 * @param maxLabels - Maximum number of labels supported
 * @returns WebGL texture containing the color map or null if creation fails
 */
function loadColorLookupTexture(
  gl: WebGL2RenderingContext,
  labelColorMap: Record<string, number>,
  maxLabels: number,
): WebGLTexture | null {
  // Create a new texture object
  const texture = gl.createTexture()
  if (!texture) {
    return null
  }

  // Create an RGBA array for the color map
  const colorMapArray = new Uint8Array(maxLabels * 4) // 4 components (RGBA) per label
  for (let i = 0; i < maxLabels; i++) {
    // Default to transparent black if a label is not in the map
    const packedColor = labelColorMap[i.toString()] || 0

    const rgba = unpackSegmentColorToRGBA(packedColor)

    // Unpack the 32-bit packed RGBA color into R, G, B, A components
    colorMapArray[i * 4 + 0] = rgba[0] // Red
    colorMapArray[i * 4 + 1] = rgba[1] // Green
    colorMapArray[i * 4 + 2] = rgba[2] // Blue
    colorMapArray[i * 4 + 3] = rgba[3] // Alpha
  }

  // Bind the texture for operations
  gl.bindTexture(gl.TEXTURE_2D, texture)
  // Set pixel storage mode (1 byte alignment)
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
  // Upload the color map data to the texture
  gl.texImage2D(
    gl.TEXTURE_2D,
    0, // Mipmap level
    gl.RGBA8, // Internal format (8 bits per channel)
    maxLabels, // Width (number of labels)
    1, // Height (1 pixel)
    0, // Border (must be 0)
    gl.RGBA, // Format of pixel data
    gl.UNSIGNED_BYTE, // Type of pixel data
    colorMapArray, // The actual pixel data
  )

  // Check for WebGL errors after texture creation
  const error = gl.getError()
  if (error !== gl.NO_ERROR) {
    console.warn('WebGL error during color map texture creation:', error)
    gl.deleteTexture(texture)
    return null
  }

  // Set texture parameters
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE) // Clamp to edge in S direction
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE) // Clamp to edge in T direction
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST) // No interpolation when minifying
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST) // No interpolation when magnifying

  // Unbind the texture
  gl.bindTexture(gl.TEXTURE_2D, null)
  return texture
}

/**
 * Interface defining the structure of WebGL program information.
 * Contains locations of attributes and uniforms used in the shader program.
 */
interface ProgramInfo {
  attribLocations: {
    vertexPosition: number
    textureCoord: number
  }
  uniformLocations: {
    uMaskTexture: WebGLUniformLocation | null
    uColorMapTexture: WebGLUniformLocation | null
    uMaxLabels: WebGLUniformLocation | null
    uTextureOrigin: WebGLUniformLocation | null
    uTextureViewSize: WebGLUniformLocation | null
  }
}

/**
 * Interface representing the camera state for rendering.
 * Contains offset, scale, and viewport dimensions.
 */
export interface WebGLCameraProxy {
  offset: IPoint
  scale: IPoint
  width: number
  height: number
}

/**
 * Interface representing the dimensions of the raster being rendered.
 */
export interface WebGLRasterDimensions {
  width: number
  height: number
}

/**
 * WebGL-based mask renderer class for efficiently displaying and updating segmentation masks.
 *
 * Features:
 * - Efficient rendering of mask data using WebGL2
 * - Support for partial updates of mask regions
 * - Customizable colors for different labels
 * - Camera transformations (pan and zoom)
 * - Alpha blending for transparency
 *
 * Usage:
 * 1. Create an instance with a WebGL2 context
 * 2. Update the mask data using updateMask()
 * 3. Set label colors using updateColorMap()
 * 4. Render the mask using render()
 * 5. Clean up resources using destroy() when done
 */
export class WebGLMaskRenderer {
  private gl: WebGL2RenderingContext
  private maxLabels: number
  private program: WebGLProgram | null = null
  private programInfo: ProgramInfo | null = null
  private maskTexture: WebGLTexture | null = null
  private colorMapTexture: WebGLTexture | null = null
  private positionBuffer: WebGLBuffer | null = null
  private textureCoordBuffer: WebGLBuffer | null = null
  private textureWidth: number | undefined
  private textureHeight: number | undefined
  /**
   * Tracks which raster's full pixel buffer is currently uploaded into
   * `maskTexture`. The renderer is shared across all rasters on a layer
   * (e.g. champion + non-champion overlays in consensus review), so when the
   * caller switches to a different raster we must re-upload its full buffer
   * even though dimensions match.
   */
  private currentRasterId: string | undefined
  private vao: WebGLVertexArrayObject | null = null

  /**
   * Creates a new WebGLMaskRenderer instance.
   * @param gl - WebGL2 rendering context
   * @param maxLabels - Maximum number of labels supported (default: 256)
   */
  constructor(gl: WebGL2RenderingContext, maxLabels: number = 256) {
    this.gl = gl
    this.maxLabels = maxLabels
    this.initWebGLResources()
  }

  /**
   * Checks for WebGL errors and throws appropriate errors if texture size limits are exceeded.
   * @param operation - Description of the operation that was performed
   */
  private checkWebGLError(operation: string): void {
    const error = this.gl.getError()
    if (error !== this.gl.NO_ERROR) {
      if (error === this.gl.INVALID_VALUE) {
        const errorMessage = `WebGL texture size limit exceeded during ${operation}. Image dimensions may be too large.`
        console.warn(`INVALID_VALUE: texImage2D: width or height out of range - ${errorMessage}`)

        throw new RasterTooLargeError(errorMessage)
      } else {
        console.warn(`WebGL error during ${operation}: ${error}`)
      }
    }
  }

  /**
   * Initializes WebGL resources including shaders, buffers, and textures.
   * Sets up the rendering pipeline with proper vertex attributes and uniforms.
   */
  private initWebGLResources(): void {
    // Initialize shader program
    const program = initShaderProgram(this.gl, vsSource, fsSource)
    if (!program) {
      console.error('Failed to initialize shader program.')
      return
    }
    this.program = program

    // Get attribute and uniform locations
    this.programInfo = {
      attribLocations: {
        vertexPosition: this.gl.getAttribLocation(this.program, 'aVertexPosition'),
        textureCoord: this.gl.getAttribLocation(this.program, 'aTextureCoord'),
      },
      uniformLocations: {
        uMaskTexture: this.gl.getUniformLocation(this.program, 'uMaskTexture'),
        uColorMapTexture: this.gl.getUniformLocation(this.program, 'uColorMapTexture'),
        uMaxLabels: this.gl.getUniformLocation(this.program, 'uMaxLabels'),
        uTextureOrigin: this.gl.getUniformLocation(this.program, 'uTextureOrigin'),
        uTextureViewSize: this.gl.getUniformLocation(this.program, 'uTextureViewSize'),
      },
    }

    // Create and bind Vertex Array Object (VAO)
    this.vao = this.gl.createVertexArray()
    this.gl.bindVertexArray(this.vao)

    // Create position buffer for a full-screen quad
    const positions = [
      -1.0,
      1.0, // Top-left
      -1.0,
      -1.0, // Bottom-left
      1.0,
      1.0, // Top-right (First triangle)
      1.0,
      1.0, // Top-right
      -1.0,
      -1.0, // Bottom-left
      1.0,
      -1.0, // Bottom-right (Second triangle)
    ]
    this.positionBuffer = this.gl.createBuffer()
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer)
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(positions), this.gl.STATIC_DRAW)
    this.gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition)
    this.gl.vertexAttribPointer(
      this.programInfo.attribLocations.vertexPosition,
      2, // 2 components per vertex (x, y)
      this.gl.FLOAT, // Data type
      false, // Don't normalize
      0, // Stride (0 = tightly packed)
      0, // Offset
    )

    // Create texture coordinate buffer
    const textureCoordinates = [
      0.0,
      0.0, // Bottom-left
      0.0,
      1.0, // Top-left
      1.0,
      0.0, // Bottom-right (First triangle)
      1.0,
      0.0, // Bottom-right
      0.0,
      1.0, // Top-left
      1.0,
      1.0, // Top-right (Second triangle)
    ]
    this.textureCoordBuffer = this.gl.createBuffer()
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.textureCoordBuffer)
    this.gl.bufferData(
      this.gl.ARRAY_BUFFER,
      new Float32Array(textureCoordinates),
      this.gl.STATIC_DRAW,
    )
    this.gl.enableVertexAttribArray(this.programInfo.attribLocations.textureCoord)
    this.gl.vertexAttribPointer(
      this.programInfo.attribLocations.textureCoord,
      2, // 2 components per vertex (s, t)
      this.gl.FLOAT, // Data type
      false, // Don't normalize
      0, // Stride (0 = tightly packed)
      0, // Offset
    )

    // Unbind VAO
    this.gl.bindVertexArray(null)

    // Enable alpha blending
    this.gl.enable(this.gl.BLEND)
    this.gl.blendFunc(this.gl.ONE, this.gl.ONE_MINUS_SRC_ALPHA) // For premultiplied alpha
  }

  /**
   * Updates the mask texture with new data.
   * Can perform either a full update or a partial update of a specific region.
   * @param imageRaster - The source image raster containing mask data
   * @param regionToUpdate - Optional region to update (for partial updates)
   */
  public updateMask(
    imageRaster: ImageRaster,
    regionToUpdate?: { x: number; y: number; width: number; height: number; data: Uint8Array },
  ): void {
    const { gl } = this
    const { width: rasterWidth, height: rasterHeight } = imageRaster
    let textureInitializedThisCall = false
    const isDifferentRaster = this.currentRasterId !== imageRaster.id

    // Initialize or re-initialize texture if needed. Also re-initialize
    // when switching to a different raster: this renderer is shared across
    // all rasters on the layer, and the texture must hold the buffer of the
    // raster being drawn — otherwise the previous raster's pixels get
    // recoloured with the new raster's colour map.
    if (
      !this.maskTexture ||
      this.textureWidth !== rasterWidth ||
      this.textureHeight !== rasterHeight ||
      isDifferentRaster
    ) {
      if (this.maskTexture) {
        gl.deleteTexture(this.maskTexture)
      }
      this.maskTexture = gl.createTexture()
      if (!this.maskTexture) {
        console.error('Failed to create mask texture.')
        return
      }
      gl.bindTexture(gl.TEXTURE_2D, this.maskTexture)
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      // Create empty texture with full dimensions
      gl.texImage2D(
        gl.TEXTURE_2D,
        0, // Mipmap level
        gl.R8, // Internal format (8-bit red channel)
        rasterWidth, // Width
        rasterHeight, // Height
        0, // Border (must be 0)
        gl.RED, // Format (single channel)
        gl.UNSIGNED_BYTE, // Type
        null, // No initial data
      )

      // Check for WebGL errors after texture creation
      this.checkWebGLError('mask texture creation')
      // Set texture parameters
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)

      this.textureWidth = rasterWidth
      this.textureHeight = rasterHeight
      this.currentRasterId = imageRaster.id
      textureInitializedThisCall = true

      // Upload full buffer
      const fullBuffer = imageRaster.getBufferForSerialization()
      gl.texSubImage2D(
        gl.TEXTURE_2D,
        0, // Mipmap level
        0, // X offset
        0, // Y offset
        rasterWidth, // Width
        rasterHeight, // Height
        gl.RED, // Format
        gl.UNSIGNED_BYTE, // Type
        fullBuffer, // Data
      )

      // Check for WebGL errors after texture upload
      this.checkWebGLError('mask texture upload')
    }

    // Handle partial update if needed
    if (regionToUpdate && regionToUpdate.data.length > 0 && !textureInitializedThisCall) {
      if (!this.maskTexture) {
        console.error('Mask texture not available for partial update.')
        return
      }
      gl.bindTexture(gl.TEXTURE_2D, this.maskTexture)
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      gl.texSubImage2D(
        gl.TEXTURE_2D,
        0, // Mipmap level
        regionToUpdate.x, // X offset
        regionToUpdate.y, // Y offset
        regionToUpdate.width, // Width
        regionToUpdate.height, // Height
        gl.RED, // Format
        gl.UNSIGNED_BYTE, // Type
        regionToUpdate.data, // Data
      )

      // Check for WebGL errors after partial texture update
      this.checkWebGLError('partial mask texture update')
    }
    gl.bindTexture(gl.TEXTURE_2D, null)
  }

  /**
   * Updates the color mapping for different labels.
   * Creates a new texture containing the color lookup table.
   * @param labelColorMap - Object mapping label indices to packed RGBA colors
   */
  public updateColorMap(labelColorMap: Record<string, number>): void {
    if (!labelColorMap) {
      console.error('labelColorMap is null or undefined.')
      return
    }
    if (this.colorMapTexture) {
      this.gl.deleteTexture(this.colorMapTexture)
    }
    this.colorMapTexture = loadColorLookupTexture(this.gl, labelColorMap, this.maxLabels)
    if (!this.colorMapTexture) {
      console.error('Failed to load color map texture.')
    }
  }

  /**
   * Renders the mask using the current camera state and raster dimensions.
   * Handles texture coordinate transformations for camera movement and zoom.
   * @param camera - Current camera state (offset, scale, viewport dimensions)
   * @param rasterDimensions - Dimensions of the raster being rendered
   */
  public render(camera: WebGLCameraProxy, rasterDimensions: WebGLRasterDimensions): void {
    if (
      !this.program ||
      !this.programInfo ||
      !this.maskTexture ||
      !this.colorMapTexture ||
      !this.positionBuffer ||
      !this.textureCoordBuffer ||
      !this.vao
    ) {
      return
    }

    const { gl, programInfo } = this
    // Set viewport to match canvas size
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)

    // Use our shader program
    gl.useProgram(this.program)

    // Bind VAO for vertex attributes
    gl.bindVertexArray(this.vao)

    // Set up texture samplers
    gl.activeTexture(gl.TEXTURE0) // Activate texture unit 0
    gl.bindTexture(gl.TEXTURE_2D, this.maskTexture)
    gl.uniform1i(programInfo.uniformLocations.uMaskTexture, 0) // Set sampler to use texture unit 0

    gl.activeTexture(gl.TEXTURE1) // Activate texture unit 1
    gl.bindTexture(gl.TEXTURE_2D, this.colorMapTexture)
    gl.uniform1i(programInfo.uniformLocations.uColorMapTexture, 1) // Set sampler to use texture unit 1

    // Calculate and set texture transform uniforms
    if (
      rasterDimensions.width > 0 &&
      rasterDimensions.height > 0 &&
      camera.scale.x > 0 &&
      camera.scale.y > 0
    ) {
      // Calculate texture coordinates based on camera transform
      const imageCoordAtViewportOriginX = (0 + camera.offset.x) / camera.scale.x
      const imageCoordAtViewportOriginY = (0 + camera.offset.y) / camera.scale.y

      const texOriginX = imageCoordAtViewportOriginX / rasterDimensions.width
      const texOriginY = imageCoordAtViewportOriginY / rasterDimensions.height

      const viewWidthInTexels = camera.width / camera.scale.x
      const viewHeightInTexels = camera.height / camera.scale.y

      const texViewSizeX = viewWidthInTexels / rasterDimensions.width
      const texViewSizeY = viewHeightInTexels / rasterDimensions.height

      // Set texture transform uniforms
      if (programInfo.uniformLocations.uTextureOrigin) {
        gl.uniform2f(programInfo.uniformLocations.uTextureOrigin, texOriginX, texOriginY)
      }
      if (programInfo.uniformLocations.uTextureViewSize) {
        gl.uniform2f(programInfo.uniformLocations.uTextureViewSize, texViewSizeX, texViewSizeY)
      }
    } else {
      // Default to full texture view
      if (programInfo.uniformLocations.uTextureOrigin) {
        gl.uniform2f(programInfo.uniformLocations.uTextureOrigin, 0.0, 0.0)
      }
      if (programInfo.uniformLocations.uTextureViewSize) {
        gl.uniform2f(programInfo.uniformLocations.uTextureViewSize, 1.0, 1.0)
      }
    }

    // Set max labels uniform
    gl.uniform1f(programInfo.uniformLocations.uMaxLabels, this.maxLabels)

    // Clear the canvas with transparent black
    gl.clearColor(0.0, 0.0, 0.0, 0.0)
    gl.clear(gl.COLOR_BUFFER_BIT)

    // Draw the quad (2 triangles)
    gl.drawArrays(gl.TRIANGLES, 0, 6)

    // Unbind VAO
    gl.bindVertexArray(null)
  }

  /**
   * Cleans up all WebGL resources used by the renderer.
   * Should be called when the renderer is no longer needed.
   */
  public destroy(): void {
    // Clean up all WebGL resources
    if (this.program) {
      this.gl.deleteProgram(this.program)
      this.program = null
    }
    if (this.maskTexture) {
      this.gl.deleteTexture(this.maskTexture)
      this.maskTexture = null
    }
    if (this.colorMapTexture) {
      this.gl.deleteTexture(this.colorMapTexture)
      this.colorMapTexture = null
    }
    if (this.positionBuffer) {
      this.gl.deleteBuffer(this.positionBuffer)
      this.positionBuffer = null
    }
    if (this.textureCoordBuffer) {
      this.gl.deleteBuffer(this.textureCoordBuffer)
      this.textureCoordBuffer = null
    }
    if (this.vao) {
      this.gl.deleteVertexArray(this.vao)
      this.vao = null
    }
    this.programInfo = null
    this.textureWidth = undefined
    this.textureHeight = undefined
  }
}
